import { createAsyncThunk } from "@reduxjs/toolkit";
import { AxiosError, isAxiosError } from "axios";
import { useRouter } from "next/navigation";
import { useDispatch } from "react-redux";

import { EDITABLE_ERROR_CODES, RenewalPage } from "@/config/app-constant";
import { APPLICATION } from "@/constants";
import cookieHealthCheck from "@/hooks/api/cookie/healthCheckCookie";
import createQuoteReference from "@/hooks/api/questions/createQuote";
import fetchQuoteData, { GetQuoteFilter } from "@/hooks/api/questions/getQuote";
import saveQuestionData from "@/hooks/api/questions/saveQuestion";
import updateQuoteReference from "@/hooks/api/questions/updateQuote";
import { MarketingConsent } from "@/types/MarketingConsent";
import { Organiser, QuestionPayload } from "@/types/QuestionPayloadType";
import { QuoteData } from "@/types/quote-data";
import { apiErrorMappings } from "@/utils/errors/api-error-map";
import handleError, {
  ErrorResponse,
} from "@/utils/renewal-question-error-handle";

type CommonTypeOfRouterAndDispatch = {
  router: ReturnType<typeof useRouter>;
  dispatch: ReturnType<typeof useDispatch>;
};

const getQuoteData = createAsyncThunk(
  "question/getQuote",
  // eslint-disable-next-line consistent-return
  async (
    {
      getQuoteFilter,
      router,
      dispatch,
    }: {
      getQuoteFilter: GetQuoteFilter;
    } & CommonTypeOfRouterAndDispatch,
    { rejectWithValue }
  ) => {
    try {
      const quote: QuestionPayload | null | undefined =
        await fetchQuoteData(getQuoteFilter);
      return quote;
    } catch (err: unknown) {
      if (
        isAxiosError(err) &&
        err.response &&
        getQuoteFilter.affiliate === "quoteInit"
      ) {
        const errorCode = err.response.data?.errors?.[0]?.code;

        if (EDITABLE_ERROR_CODES.includes(errorCode)) {
          return err.response.data.data as QuestionPayload;
        }
      }

      if (APPLICATION === RenewalPage) {
        handleError(err as AxiosError<ErrorResponse>, router, dispatch);

        return null;
      }
      const errorObj = apiErrorMappings(err as AxiosError);
      if (errorObj?.errors?.[0]?.code === 4029) return null;
      if (errorObj.httpCode !== 401) errorObj.multiButton = true;
      // You can choose to use the message attached to err or write a custom error
      return rejectWithValue(errorObj);
    }
  }
);

const createQuoteOnQuestion = createAsyncThunk(
  "question/createQuote",
  async (
    {
      payload,
      router,
      dispatch,
    }: { payload: QuestionPayload } & CommonTypeOfRouterAndDispatch,
    { rejectWithValue }
  ) => {
    try {
      const updatedPayload = {
        ...payload,
        viewPage: payload.viewPage || null,
      };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const quote: QuestionPayload | null | undefined =
        await createQuoteReference(updatedPayload);
      return quote;
    } catch (err: unknown) {
      if (APPLICATION === RenewalPage) {
        handleError(err as AxiosError<ErrorResponse>, router, dispatch);

        return null;
      }
      const errorObj = apiErrorMappings(err as AxiosError);
      if (errorObj.httpCode !== 401) errorObj.multiButton = true;

      if (errorObj.httpCode === 400) {
        errorObj.validationEnable = true;
      }
      // You can choose to use the message attached to err or write a custom error
      return rejectWithValue(errorObj);
    }
  }
);

const updateQuoteOnQuestion = createAsyncThunk(
  "question/updateQuote",
  async (
    {
      payload,
      router,
      dispatch,
    }: { payload: QuestionPayload } & CommonTypeOfRouterAndDispatch,
    { rejectWithValue, getState }
  ) => {
    try {
      const state = getState() as { quote: QuoteData };
      const updatedPayload = {
        ...payload,
        version: state?.quote?.version ?? payload?.version,
        attributes: {
          ...payload?.attributes,
          tripDetails: {
            ...payload?.attributes?.tripDetails,
            policyPackage:
              state?.quote?.attributes.tripDetails?.policyPackage ??
              payload?.attributes.tripDetails?.policyPackage,
          },
          marketingConsent: {
            ...((state?.quote?.attributes.marketingConsent ??
              payload?.attributes
                ?.marketingConsent) as unknown as MarketingConsent),
          },
          organiser: {
            ...(payload?.attributes?.organiser as unknown as Organiser),
            telephone:
              state?.quote?.attributes?.organiser?.telephone ??
              payload?.attributes?.organiser?.telephone,
          },
        },
        viewPage: payload.viewPage || null,
      };

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const quote: QuestionPayload | null | undefined =
        await updateQuoteReference(updatedPayload);

      return quote;
    } catch (err: unknown) {
      if (APPLICATION === RenewalPage) {
        handleError(err as AxiosError<ErrorResponse>, router, dispatch);

        return null;
      }
      const errorObj = apiErrorMappings(err as AxiosError);
      if (errorObj.httpCode !== 401) errorObj.multiButton = true;
      if (errorObj.httpCode === 400) {
        errorObj.validationEnable = true;
      }
      // You can choose to use the message attached to err or write a custom error
      return rejectWithValue(errorObj);
    }
  }
);

const saveQuestionDataBFF = createAsyncThunk(
  "question/saveQuestionDataBFF",
  async (
    {
      payload,
      dispatch,
      router,
    }: { payload: QuestionPayload } & CommonTypeOfRouterAndDispatch,
    { rejectWithValue }
  ) => {
    try {
      const updatedPayload = {
        ...payload,
        viewPage: payload.viewPage || null,
      };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const quote: QuestionPayload | null | undefined =
        await saveQuestionData(updatedPayload);
      return quote;
    } catch (err: unknown) {
      if (APPLICATION === RenewalPage) {
        handleError(err as AxiosError<ErrorResponse>, router, dispatch);

        return null;
      }
      const errorObj = apiErrorMappings(err as AxiosError);
      if (errorObj.httpCode !== 401) errorObj.multiButton = true;
      // You can choose to use the message attached to err or write a custom error
      return rejectWithValue(errorObj);
    }
  }
);

const sessionCheck = createAsyncThunk(
  "question/sessionCheck",
  async (
    { dispatch, router }: CommonTypeOfRouterAndDispatch,
    { rejectWithValue }
  ) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const quote: QuestionPayload | null | undefined =
        await cookieHealthCheck();
      return quote;
    } catch (err: unknown) {
      if (APPLICATION === RenewalPage) {
        handleError(err as AxiosError<ErrorResponse>, router, dispatch);

        return null;
      }
      const errorObj = apiErrorMappings(err as AxiosError);
      if (errorObj.httpCode !== 401) errorObj.multiButton = true;
      // You can choose to use the message attached to err or write a custom error
      return rejectWithValue(errorObj);
    }
  }
);

export {
  getQuoteData,
  createQuoteOnQuestion,
  updateQuoteOnQuestion,
  saveQuestionDataBFF,
  sessionCheck,
};
